import { Button, Form, Input, Select, Tag, Upload } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createUniversity } from "@/redux/rtk/features/CRM/university/universitySlice";
import { loadAllMajor, createMajor } from "@/redux/rtk/features/CRM/major/majorSlice";
import Card from "@/UI/Card";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { toast } from "react-hot-toast";

export default function AddUniversity() {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [selectedMajors, setSelectedMajors] = useState([]);
    const [newMajorName, setNewMajorName] = useState("");
    const [isCreatingMajor, setIsCreatingMajor] = useState(false);
    const [fileList, setFileList] = useState([]);

    const { list: majorList, loading: majorLoading } = useSelector(
        (state) => state.major
    );
    const { loading: universityLoading } = useSelector(
        (state) => state.university
    );

    useEffect(() => {
        dispatch(loadAllMajor({ query: "all" }));
    }, [dispatch]);

    const onFinish = async (values) => {
        const formData = new FormData();
        formData.append("universityName", values.universityName);
        formData.append("email", values.email);
        if (values.website) formData.append("website", values.website);
        if (values.youtubeLink) formData.append("youtubeLink", values.youtubeLink);
        if (values.location) formData.append("location", values.location);

        selectedMajors.forEach((id) => {
            formData.append("majorIds[]", id);
        });

        fileList.forEach((file) => {
            formData.append("file", file.originFileObj);
        });

        const resp = await dispatch(createUniversity(formData));
        // if (resp.payload.message === "University Created Successfully") {
        {
            toast.success(t("university.universityCreatedSuccess"));
            form.resetFields();
            setSelectedMajors([]);
            setFileList([]);
            navigate("/admin/crm/university");
        }
    };

    const handleCreateMajor = async () => {
        const trimmedName = newMajorName.trim();
        if (!trimmedName) return;

        // Check for duplicate name locally
        const existingMajor = majorList?.find(
            major => major.name.toLowerCase() === trimmedName.toLowerCase()
        );

        if (existingMajor) {
            toast.success(t("university.majorAlreadyExistsAndSelected") || "Major already exists and has been selected");
            if (!selectedMajors.includes(existingMajor.id)) {
                setSelectedMajors(prev => [...prev, existingMajor.id]);
            }
            setNewMajorName("");
            return;
        }

        setIsCreatingMajor(true);
        try {
            const resp = await dispatch(createMajor({ name: trimmedName }));
            if (resp.payload.message === "success") {
                toast.success(t("major.majorCreatedSuccess") || "Major Created Successfully");
                const newMajor = resp.payload.data;
                setSelectedMajors(prev => [...prev, newMajor.id]);
                setNewMajorName("");
                await dispatch(loadAllMajor({ query: "all" }));
            }
        } catch (error) {
            console.error("Failed to create major:", error);
        } finally {
            setIsCreatingMajor(false);
        }
    };

    const onFinishFailed = (errorInfo) => {
        console.log("Failed:", errorInfo);
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <Card className="max-w-4xl mx-auto">
                <div className="border-b pb-4 mb-6">
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {t("university.addUniversity")}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {t("university.addUniversityDescription")}
                    </p>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            label={t("university.universityName")}
                            name="universityName"
                            rules={[
                                {
                                    required: true,
                                    message: t("university.universityNameRequired"),
                                },
                            ]}
                        >
                            <Input
                                size="large"
                                placeholder={t("university.universityNamePlaceholder")}
                            />
                        </Form.Item>

                        <Form.Item
                            label={t("common.email")}
                            name="email"
                            rules={[
                                {
                                    required: true,
                                    message: t("university.emailRequired"),
                                },
                                {
                                    type: "email",
                                    message: t("university.emailInvalid"),
                                },
                            ]}
                        >
                            <Input
                                size="large"
                                placeholder={t("university.emailPlaceholder")}
                            />
                        </Form.Item>

                        <Form.Item
                            label={t("university.website")}
                            name="website"
                        >
                            <Input
                                size="large"
                                placeholder={t("university.websitePlaceholder")}
                            />
                        </Form.Item>

                        <Form.Item
                            label={t("university.youtube_link")}
                            name="youtubeLink"
                        >
                            <Input
                                size="large"
                                placeholder={t("university.youtubePlaceholder")}
                            />
                        </Form.Item>

                        <Form.Item
                            label={t("university.location")}
                            name="location"
                        >
                            <Input
                                size="large"
                                placeholder={t("university.locationPlaceholder")}
                            />
                        </Form.Item>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <Form.Item
                            label={t("university.file")}
                        >
                            <Upload
                                beforeUpload={() => false}
                                fileList={fileList}
                                onChange={({ fileList }) => setFileList(fileList)}
                                accept=".pdf"
                                multiple
                            >
                                <Button icon={<UploadOutlined />} size="large">
                                    {t("university.uploadPdf")}
                                </Button>
                            </Upload>
                        </Form.Item>
                    </div>

                    <Form.Item
                        label={t("university.majors")}
                        className="mt-4"
                    >
                        <Select
                            mode="multiple"
                            size="large"
                            loading={majorLoading}
                            placeholder={t("university.selectMajors")}
                            value={selectedMajors}
                            onChange={setSelectedMajors}
                        >
                            {majorList?.map((major) => (
                                <Select.Option key={major.id} value={major.id}>
                                    {major.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>


                    <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
                        <Button
                            size="large"
                            onClick={() => navigate("/admin/crm/university")}
                        >
                            {t("common.cancel")}
                        </Button>
                        <Button type="primary" size="large" htmlType="submit" loading={universityLoading}>
                            {t("university.createUniversity")}
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
}
